import { Component, computed, ElementRef, HostListener, inject, signal } from "@angular/core";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { EarnTableStateEnum } from "@bitwarden/web-vault/app/models/enum/earn.enum";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { TokenService } from "@bitwarden/common/abstractions/token.service";
import { EarnService } from "@bitwarden/web-vault/app/services/earn/earn.service";
import { EarnTableView } from "@bitwarden/web-vault/app/models/types/transaction.types";
import {
  ConfirmationDialogService,
  CtaNotificationData,
} from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { IButton } from "@bitwarden/web-vault/app/models/interfaces/button.interface";
import { ButtonClass } from "@bitwarden/web-vault/app/models/types/button.types";
import { EarnDraftTable } from "@bitwarden/web-vault/app/components/tables/earn-tables/tables/draft/earn-draft.table";
import { EarnViewTable } from "@bitwarden/web-vault/app/components/tables/earn-tables/tables/view/earn-view.table";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { EarnLookup } from "@bitwarden/web-vault/app/services/earn/lookup.service";

export type PublishedData = {
  description: string | null;
  transactionDate: string | null;
  amount: number | null;
  account: string | null;
  inOut: string | null;
  currency: string | null;
  source: string | null;
  institution: string | null;
};

@Component({
  selector: "app-create-and-publish",
  templateUrl: "./create-and-publish.component.html",
})
export class CreateAndPublishComponent {
  /** Services & stores */
  private transactionStore = inject(TransactionStoreService);
  protected i18nService = inject(I18nService);
  protected dataRepositoryService = inject(DataRepositoryService);
  protected tokenService = inject(TokenService);
  protected earnService = inject(EarnService);
  protected earnLookup = inject(EarnLookup);
  protected confirmationService = inject(ConfirmationDialogService);
  protected appState = inject(AppStateStoreService);
  private host = inject(ElementRef<HTMLElement>);

  /** Data & table */
  protected transactions: TransactionView[] = this.transactionStore.enrichTransaction.collection();
  protected draftTable: EarnDraftTable = new TablesFactory().get(TablesType.RedactionDraft, this);
  protected viewTable: EarnViewTable = new TablesFactory().get(TablesType.RedactionView, this);

  private isNewTransactions = signal(false);

  previewData = computed<EarnTableView[]>(() => {
    const mask = this.earnService.selectedMask();
    const rows = this.draftTable.data();
    const ids = new Set(mask?.appliedToArray ?? []);
    return rows.filter((r) => ids.has(r.id));
  });

  draftData = computed<EarnTableView[]>(() => {
    const mask = this.earnService.selectedMask();
    const rows = this.draftTable.data();
    const ids = new Set(mask?.appliedToArray ?? []);
    if (this.isNewTransactions()) {
      return rows;
    }
    return rows.filter((r) => ids.has(r.id));
  });

  /** Visibility menu state */
  showVisibilityMenu = signal(false);

  rowsMasked = computed(() => {
    return this.earnLookup.lookupMask().rows.size;
  });

  cellsMasked = computed<number>(() => {
    return Array.from(this.earnLookup.lookupMask().cells.values()).reduce(
      (sum, value) => sum + value.size,
      0,
    );
  });

  columnsMasked = computed(() => {
    return this.earnLookup.lookupMask().columns.size;
  });

  draftButtonOptions = this.makeButtonOptions("saveAsDraft", this.preSaveDraft);
  previewButtonOptions = this.makeButtonOptions("preview", this.preview, "primary");
  publishButtonOptions = this.makeButtonOptions("publish", this.prePublish, "primary");
  editAsNewButtonOptions = this.makeButtonOptions("editAsNew", this.editAsNew, "primary");

  /**
   * In-flight guard against double-submits. The blocking overlay no longer blocks input once the
   * store is initialised (#5459), so a second click would re-enter the save (#5468). saveAsDraft
   * also disables its button for feedback via a fresh options reference (app-gloss-button is OnPush,
   * so an in-place `isEnabled` mutation wouldn't re-render). It also gates preview() so a Preview
   * click mid-save can't rewrite selectedMask out from under the running draft save (#5527).
   */
  private savingDraft = false;

  private makeButtonOptions(textKey: string, handler: () => void, buttonClass?: ButtonClass) {
    return {
      text: this.i18nService.t(textKey),
      onClick: handler.bind(this),
      isEnabled: true,
      ...(buttonClass && { class: buttonClass }),
    } as IButton;
  }

  private async preSaveDraft() {
    const i18nService = this.confirmationService["i18nService"];
    const data: CtaNotificationData = {
      title: i18nService.t("headsUp"),
      message: i18nService.t("youWannaSaveDraftOrPublish"),
      cancel: { method: () => this.preview(), title: i18nService.t("publishNow") },
      confirm: {
        method: () => this.saveAsDraft(),
        title: i18nService.t("saveAsDraft"),
      },
      reminder: null,
    };
    await this.confirmationService.ctaNotification(data);
  }

  private async prePublish() {
    const i18nService = this.confirmationService["i18nService"];
    const data: CtaNotificationData = {
      title: i18nService.t("areYouSureYouWantToPublish"),
      message: i18nService.t("makeSureYourDataHasBeenProperlyReviewedAndIsReadyToBeShared"),
      cancel: { method: () => {}, title: i18nService.t("backToPreview") },
      confirm: {
        method: () => this.publish(),
        title: i18nService.t("publish"),
      },
      reminder: null,
    };
    await this.confirmationService.ctaNotification(data);
  }

  private async saveAsDraft() {
    // Ignore re-entry while a draft save is in flight (#5468); disable the button for feedback.
    if (this.savingDraft) {
      return;
    }
    this.savingDraft = true;
    // Fresh reference so the OnPush gloss-button re-renders as disabled (an in-place mutation wouldn't).
    this.draftButtonOptions = { ...this.draftButtonOptions, isEnabled: false };
    try {
      this.earnService.lookupToMask();
      const processMessage = `Saving draft ...`;
      this.appState.startProcess("sharing-data", processMessage);
      this.earnService.makeSelectedDraft();
      //this.draftTable.applyNewArrayToMask();
      await this.earnService.saveSelectedMask();
      await this.earnService.updateState();
      this.confirmationService.notify("success", "saved", "yourDataIsSavedAsDraft");
      this.earnService.activeSection.set("history");
    } catch (e) {
      this.confirmationService.notify("error", "darnIt", "somethingWentTerriblyWrong");
    } finally {
      this.appState.endProcess("sharing-data");
      this.savingDraft = false;
      this.draftButtonOptions = { ...this.draftButtonOptions, isEnabled: true };
    }
  }

  private preview() {
    // Don't rewrite selectedMask while a draft save is reading it — Preview stays enabled during the
    // save (only the Draft button is disabled), so a click here mid-save would corrupt it (#5527).
    if (this.savingDraft) {
      return;
    }
    // generate mask from lookupMask set it to selectedMask and go to preview
    this.draftTable.applyNewArrayToMask();
    this.earnService.lookupToMask();
    this.earnService.breadCrumbState.set(EarnTableStateEnum.PREVIEW);
  }

  private async publish() {
    // No in-flight guard needed: savePublishedReport() sets its own `saving` flag synchronously
    // before its first await, and the pre-guard part of publish() only touches updatedAt. The real
    // double-submit target is the CTA confirm button — CtaNotificationComponent closes the dialog then
    // calls the method, and MatDialog stays clickable during the close animation — but that second
    // call is absorbed by savePublishedReport()'s `saving` flag, not guarded at the dialog.
    this.earnService.lookupToMask();
    this.earnService.breadCrumbState.set(EarnTableStateEnum.PUBLISH);
    this.viewTable.applyNewArrayToMask();
    await this.viewTable.savePublishedReport();
  }

  private async editAsNew() {
    this.earnService.lookupToMask();
    this.earnService.editAsNew();
  }

  addLatestTransaction() {
    this.isNewTransactions.set(true);
    this.earnService.updateMaskForLatestTransactions();
    const rows = this.draftTable.data();
    this.earnLookup.applySearchFilterToLookup(rows.map((r) => r.id));
  }

  /**
   * TODO Cleanup the visibility actions buttons
   * */
  toggleVisibilityMenu() {
    this.showVisibilityMenu.update((v) => !v);
  }
  closeVisibilityMenu() {
    this.showVisibilityMenu.set(false);
  }

  /** Close on outside click / Esc */
  @HostListener("document:click", ["$event"])
  handleOutsideClick(event: MouseEvent) {
    const el = this.host.nativeElement;
    if (!el.contains(event.target as Node)) {
      this.closeVisibilityMenu();
    }
  }
  @HostListener("document:keydown.escape")
  handleEsc() {
    this.closeVisibilityMenu();
  }

  /** Dropdown actions */
  onResetToLastPublished() {
    const list = this.earnService.maskList() ?? [];
    const last = [...list]
      .filter((m) => m.status === "published")
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())[0];
    if (last) {
      this.earnService.selectedMask.set(last);
      this.draftTable.gridApi?.redrawRows();
      this.draftTable.gridApi?.refreshHeader();
    }
    this.closeVisibilityMenu();
  }

  onRedactColumns() {
    /* this.table?.earnTableService?.startRedaction?.("columns");*/
    this.closeVisibilityMenu();
  }
  onRedactCells() {
    this.closeVisibilityMenu();
  }
  onRedactRows() {
    this.closeVisibilityMenu();
  }
  onUnredactAll() {
    this.closeVisibilityMenu();
  }
  onUnredactCells() {
    this.closeVisibilityMenu();
  }
  onUnredactColumns() {
    this.closeVisibilityMenu();
  }
  onUnredactRows() {
    this.closeVisibilityMenu();
  }
}
